import React, { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import {
  MailIcon,
  LockIcon,
  EyeIcon,
  EyeOffIcon,
  ShieldCheckIcon,
  FileTextIcon,
  GrowthChartIcon,
  ArrowRightIcon,
  AlertCircleIcon,
  CheckBadgeIcon,
  ClockTimerIcon,
  UsersGroupIcon,
} from "../components/Icons";
import logo from "../assets/logo.png";
import loginCoupleImg from "../assets/login-couple.png";
import SEO from "../components/SEO";
import { API_URL } from "../services/api";

function Login({ onLoginSuccess }) {
  const history = useHistory();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigateByStatus = (userData) => {
    const status = userData?.vendorStatus || "none";
    if (status === "approved") {
      history.push("/vendor/dashboard");
    } else if (status === "pending" || status === "rejected" || status === "suspended") {
      history.push("/vendor/status");
    } else {
      history.push("/vendor/apply");
    }
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) { setError("Please enter your email address."); return; }
    if (!password) { setError("Please enter your password."); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/Login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      if (response.data.success) {
        if (response.data.token && response.data.user) {
          const user = response.data.user;
          const token = response.data.token;
          if (onLoginSuccess) onLoginSuccess(user, token);
          navigateByStatus(user);
        } else {
          setStep(2);
        }
      } else {
        setError(response.data.msg || "Incorrect email or password. Please try again.");
      }
    } catch (err) {
      const serverMsg = err.response?.data?.msg || err.response?.data?.message;
      if (serverMsg) {
        setError(serverMsg);
      } else if (!err.response && err.message?.includes("Network Error")) {
        setError("Unable to connect to backend server. Please verify the backend is running on port 5000.");
      } else {
        setError(serverMsg || "Incorrect email or password. Please check your credentials.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!otp.trim() || otp.trim().length < 4) { setError("Please enter the complete verification code."); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/Login/verify-otp`, {
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
      });
      if (response.data.success) {
        if (response.data.token && response.data.user) {
          if (onLoginSuccess) onLoginSuccess(response.data.user, response.data.token);
          navigateByStatus(response.data.user);
        } else {
          setError(response.data.msg || "Invalid verification response.");
        }
      } else {
        setError(response.data.msg || "Invalid verification code. Please try again.");
      }
    } catch (err) {
      const serverMsg = err.response?.data?.msg || err.response?.data?.message;
      setError(serverMsg || "Invalid verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lp-wrapper">
      <SEO
        title="Seller Login | IM Online Seller Center"
        description="Sign in to your IM Online Seller Center to manage footwear inventory, process customer orders, view analytics, and request bank payouts."
        canonical="https://vendor.imonline.shop/vendor/login"
        ogTitle="IM Online Seller Center Login"
        ogDescription="Access your footwear store dashboard, track orders, and monitor your revenue."
      />
      {/* ── HEADER ── */}
      <header className="lp-header">
        <div className="lp-header-inner">
          <div className="lp-header-brand">
            <img src={logo} alt="IM Online" className="lp-header-logo" />
            <div className="lp-header-brand-text">
              <span className="lp-brand-name">IM ONLINE</span>
              <span className="lp-brand-subtitle">SELLER CENTER</span>
            </div>
          </div>
          <div className="lp-header-right">
            <span className="lp-header-note">New seller?</span>
            <Link to="/vendor/register" className="lp-header-create-btn">
              Create Account
            </Link>
          </div>
        </div>
      </header>

      {/* ── MAIN HERO AREA ── */}
      <main className="lp-main">
        <div className="lp-container">

          {/* ── TWO COLUMN GRID ── */}
          <div className="lp-hero-grid">

            {/* ════ LEFT SIDE: Branding + model image ════ */}
            <div className="lp-left-col">

              {/* Text content block (top-left) */}
              <div className="lp-left-text">
                <span className="lp-badge">SELLER CENTER</span>

                <h1 className="lp-headline">
                  Start Selling.<br />
                  <span className="lp-headline-red">Grow</span> Your Business.
                </h1>

                <p className="lp-desc">
                  Everything you need to start selling online — from setting up your store to managing your products and growing your business.
                </p>

                {/* Green trust pill */}
                <div className="lp-trust-pill">
                  <span className="lp-green-dot" />
                  <span>Empowering Indian Small Businesses &amp; Retailers</span>
                </div>

                {/* 3 Benefit Cards */}
                <div className="lp-benefits">
                  <div className="lp-benefit-item">
                    <div className="lp-benefit-icon">
                      <ShieldCheckIcon size={20} color="#a85050" />
                    </div>
                    <div>
                      <p className="lp-benefit-title">Easy Onboarding</p>
                      <p className="lp-benefit-desc">Start with simple seller verification.</p>
                    </div>
                  </div>
                  <div className="lp-benefit-item">
                    <div className="lp-benefit-icon">
                      <FileTextIcon size={20} color="#a85050" />
                    </div>
                    <div>
                      <p className="lp-benefit-title">Minimal Documentation</p>
                      <p className="lp-benefit-desc">Get started with the essential documents.</p>
                    </div>
                  </div>
                  <div className="lp-benefit-item">
                    <div className="lp-benefit-icon">
                      <GrowthChartIcon size={20} color="#a85050" />
                    </div>
                    <div>
                      <p className="lp-benefit-title">Built to Help You Grow</p>
                      <p className="lp-benefit-desc">Tools and guidance for your seller journey.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Model image — bottom right of left col */}
              <div className="lp-model-wrap">
                {/* Peach radial circle behind model */}
                <div className="lp-model-bg-circle" />
                {/* Decorative dots */}
                <div className="lp-model-dots" />
                {/* Floating accent dots */}
                <span className="lp-dot lp-dot-1" />
                <span className="lp-dot lp-dot-2" />
                {/* Model image */}
                <img
                  src={loginCoupleImg}
                  alt="IM Online Sellers"
                  className="lp-model-img"
                />
              </div>

              {/* Footnote below model */}
              <div className="lp-left-footnote">
                <ShieldCheckIcon size={14} color="#999" />
                <span>Trusted platform for Indian small businesses and independent sellers.</span>
              </div>
            </div>

            {/* ════ RIGHT SIDE: Login Card ════ */}
            <div className="lp-right-col">
              {/* Soft rose glow behind card */}
              <div className="lp-card-glow" />

              <div className="lp-card">
                {/* Card header */}
                <div className="lp-card-header">
                  <div className="lp-lock-box">
                    <LockIcon size={22} color="#a85050" />
                  </div>
                  <div>
                    <h2 className="lp-card-title">
                      {step === 1 ? "Welcome back" : "Enter Verification Code"}
                    </h2>
                    <p className="lp-card-sub">
                      {step === 1
                        ? "Sign in to manage your IM Online store."
                        : `Enter the 6-digit code sent to ${email}`}
                    </p>
                  </div>
                </div>

                {/* Error alert */}
                {error && (
                  <div className="lp-error">
                    <AlertCircleIcon size={16} color="#dc2626" />
                    <span>{error}</span>
                  </div>
                )}

                {step === 1 ? (
                  <form onSubmit={handleCredentialsSubmit}>
                    {/* Email */}
                    <div className="lp-field">
                      <label className="lp-label" htmlFor="lp-email">Email Address</label>
                      <div className="lp-input-wrap">
                        <span className="lp-input-icon"><MailIcon size={18} /></span>
                        <input
                          id="lp-email"
                          type="email"
                          className="lp-input"
                          placeholder="Enter your email address"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          autoComplete="email"
                          required
                        />
                      </div>
                    </div>

                    {/* Password */}
                    <div className="lp-field">
                      <label className="lp-label" htmlFor="lp-pass">Password</label>
                      <div className="lp-input-wrap">
                        <span className="lp-input-icon"><LockIcon size={18} /></span>
                        <input
                          id="lp-pass"
                          type={showPassword ? "text" : "password"}
                          className="lp-input lp-input-eye"
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="current-password"
                          required
                        />
                        <button
                          type="button"
                          className="lp-eye-btn"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label="Toggle password"
                        >
                          {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                        </button>
                      </div>
                    </div>

                    {/* Remember + Forgot */}
                    <div className="lp-options-row">
                      <label className="lp-remember">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="lp-checkbox"
                        />
                        <span>Remember me</span>
                      </label>
                      <a
                        href="mailto:support@imonline.shop?subject=Password Reset"
                        className="lp-forgot"
                      >
                        Forgot password?
                      </a>
                    </div>

                    {/* Sign In button */}
                    <button type="submit" className="lp-signin-btn" disabled={loading}>
                      {loading ? (
                        <span>
                          <span className="spinner-border spinner-border-sm me-2" role="status" />
                          Signing In...
                        </span>
                      ) : (
                        <span className="lp-btn-inner">
                          Sign In <ArrowRightIcon size={18} />
                        </span>
                      )}
                    </button>

                    {/* Divider */}
                    <div className="lp-divider">
                      <span className="lp-divider-text">New to IM Online?</span>
                    </div>

                    {/* Create account */}
                    <Link to="/vendor/register" className="lp-create-btn">
                      Create Seller Account
                    </Link>
                  </form>
                ) : (
                  <form onSubmit={handleOtpSubmit}>
                    <div className="lp-field" style={{ textAlign: "center" }}>
                      <label className="lp-label" htmlFor="lp-otp" style={{ display: "block", textAlign: "center" }}>
                        6-Digit Verification Code
                      </label>
                      <input
                        id="lp-otp"
                        type="text"
                        className="lp-otp-input"
                        maxLength="6"
                        placeholder="• • • • • •"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                        autoFocus
                        required
                      />
                      <p style={{ fontSize: "13px", color: "#888", marginTop: "8px" }}>
                        Enter the code sent to your registered email.
                      </p>
                    </div>
                    <button type="submit" className="lp-signin-btn" disabled={loading}>
                      {loading ? "Verifying..." : "Verify & Sign In"}
                    </button>
                    <button type="button" className="lp-back-btn" onClick={() => setStep(1)}>
                      ← Back to Sign In
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* ── TRUST STRIP ── */}
          <div className="lp-trust-strip">
            <div className="lp-trust-item">
              <div className="lp-trust-icon"><ShieldCheckIcon size={22} color="#a85050" /></div>
              <div>
                <p className="lp-trust-title">Secure &amp; Trusted</p>
                <p className="lp-trust-desc">Your data is always protected</p>
              </div>
            </div>
            <div className="lp-trust-item">
              <div className="lp-trust-icon"><CheckBadgeIcon size={22} color="#a85050" /></div>
              <div>
                <p className="lp-trust-title">No Hidden Charges</p>
                <p className="lp-trust-desc">Zero subscription or onboarding fees</p>
              </div>
            </div>
            <div className="lp-trust-item">
              <div className="lp-trust-icon"><ClockTimerIcon size={22} color="#a85050" /></div>
              <div>
                <p className="lp-trust-title">Quick Verification</p>
                <p className="lp-trust-desc">Get verified and start selling faster</p>
              </div>
            </div>
            <div className="lp-trust-item">
              <div className="lp-trust-icon"><UsersGroupIcon size={22} color="#a85050" /></div>
              <div>
                <p className="lp-trust-title">Growing Community</p>
                <p className="lp-trust-desc">Join 5,000+ successful sellers</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* ── FOOTER ── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <span className="lp-footer-copy">&copy; 2026 IM Online. All rights reserved.</span>
          <div className="lp-footer-links">
            <Link to="/vendor#how-it-works">How It Works</Link>
            <Link to="/vendor#pricing">Pricing</Link>
            <Link to="/vendor/requirements">Seller Requirements</Link>
            <Link to="/vendor#faq">FAQ</Link>
            <a href="mailto:support@imonline.shop">Seller Support</a>
            <a href="https://www.imonline.shop" target="_blank" rel="noreferrer">Customer Website &#x2197;</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Login;
